// Reine Rechenfunktionen fuer den Statistik-Bildschirm (IMG_4884 bis
// 4888, 01.09.2026): je Zeile ein kleiner Ring mit "X von Y gesehen".
//
// Laenderkennzeichen (scope 'country') zaehlen laut E9 in
// docs/decisions.md weder in ein Bundesland noch in ihr Sitzland hinein,
// nur in ihre eigene Kategorie (siehe fortschrittFuer() in treffer.ts).

import type { Erdteil, Kennzeichen, Land } from "./bundle.js";
import type { Eintragskarte } from "./entries.js";

export interface RingZeile {
  name: string;
  gesehen: number;
  gesamt: number;
  /** Ziel der Zeile: jede Zeile fuehrt auf ihre eigene Unterseite. */
  bereich: Bereich;
}

function sortiertDeutsch(zeilen: RingZeile[]): RingZeile[] {
  return zeilen.sort((a, b) => a.name.localeCompare(b.name, "de"));
}

/**
 * Je Bundesland eine Zeile. Ein Zeichen, das sich mehrere Bundeslaender
 * teilt (Grenzkreise), zaehlt in jedem davon mit - genau wie der
 * Abgleich in data/build/kennzeichen-de.mjs das schon gegen die
 * Original-App verifiziert (785 Treffer exakt), nicht als eigene
 * Mehrfach-Gruppe "Land A, Land B".
 *
 * FALLE: Diese Liste steht unter dem Reiter DEUTSCHLAND und darf deshalb
 * nur deutsche Zeichen sehen. Ohne die Einschraenkung auf das Land landen
 * die 16 polnischen Woiwodschaften und die 15 norwegischen Fylker
 * zwischen den Bundeslaendern, denn state_name ist nicht auf Deutschland
 * beschraenkt und scope ist bei ihnen genauso "region".
 *
 * FALLE: "gesamt" und "gesehen" zaehlen nicht dieselbe Spalte. Ein Zeichen,
 * das sein Bundesland gewechselt hat, steht in der Original-App in beiden
 * Kreislisten, gilt aber nur einmal als gesehen, naemlich dort, wo es heute
 * ausgegeben wird. HZ zaehlt also in Brandenburg mit, laesst sich dort aber
 * nicht abhaken; abgehakt wird es in Sachsen-Anhalt. Ohne diese Trennung
 * meldete Brandenburg 44 von 55 statt 43 von 55.
 */
export function bundeslandRinge(
  kennzeichen: Kennzeichen[],
  eintraege: Eintragskarte,
  deutschlandId: number | undefined,
): RingZeile[] {
  const gruppen = new Map<string, RingZeile>();
  const zeileFuer = (name: string) => {
    const zeile = gruppen.get(name) ?? {
      name,
      gesehen: 0,
      gesamt: 0,
      bereich: { art: "bundesland" as const, name },
    };
    gruppen.set(name, zeile);
    return zeile;
  };
  if (deutschlandId === undefined) return [];
  for (const k of kennzeichen) {
    if (k.countryId !== deutschlandId) continue;
    if (k.scope === "country" || !k.stateName) continue;
    for (const name of k.stateName.split(", ")) zeileFuer(name).gesamt++;
    if (!eintraege.has(k.id)) continue;
    // Faellt stateNameCurrent aus (aeltere Bundles ohne die Spalte), gilt
    // wieder die volle Liste: lieber eins zu viel als gar nicht gezaehlt.
    for (const name of (k.stateNameCurrent ?? k.stateName).split(", "))
      zeileFuer(name).gesehen++;
  }
  return sortiertDeutsch([...gruppen.values()]);
}

/** Je Land eines Erdteils eine Zeile, ohne Deutschland (eigener Reiter). */
export function laenderRinge(
  kennzeichen: Kennzeichen[],
  laender: Land[],
  eintraege: Eintragskarte,
  erdteil: Erdteil,
): RingZeile[] {
  const zeilen = laender
    .filter((l) => l.iso2 !== "DE" && l.continent === erdteil)
    .map((land) => {
      const passende = kennzeichen.filter(
        (k) => k.countryId === land.id && k.scope !== "country",
      );
      return {
        name: land.nameDe,
        gesehen: passende.filter((k) => eintraege.has(k.id)).length,
        gesamt: passende.length,
        bereich: { art: "land" as const, iso2: land.iso2 },
      };
    });
  return sortiertDeutsch(zeilen);
}

// --- Unterseiten der Statistik (docs/reference/statistik-detail-*.png,
// 08.09.2026) -----------------------------------------------------------
//
// Jede Zeile der Statistik fuehrt auf eine eigene Seite mit den einzelnen
// Kennzeichen, getrennt nach "Gesehen" und "Nicht gesehen". Welche Zeichen
// dazugehoeren, muss dieselbe Funktion beantworten, die auch den Ring
// rechnet - sonst zeigt die Liste etwas anderes an als die Zahl darueber.

/** Die drei Reiter der Statistik. */
export const REITER_SCHLUESSEL = ["deutschland", "europa", "weitere"] as const;
export type StatistikReiter = (typeof REITER_SCHLUESSEL)[number];

/** Worauf sich eine Statistik-Zeile bezieht. */
export type Bereich =
  | { art: "land"; iso2: string }
  | { art: "bundesland"; name: string }
  | { art: "bundesweit" }
  | { art: "diplomaten" }
  | { art: "laenderkennzeichen" }
  /** Alles unter einem Reiter, fuer die Zeile "Nur in der eigenen Sammlung". */
  | { art: "fehlend"; reiter: StatistikReiter };

/**
 * Alle Kennzeichen, die unter einem Reiter stehen. Die Laenderkennzeichen
 * gehoeren zu EUROPA, dort steht ihre Zeile.
 */
export function reiterKennzeichen(
  reiter: StatistikReiter,
  kennzeichen: Kennzeichen[],
  laender: Land[],
): Kennzeichen[] {
  const landZu = new Map(laender.map((l) => [l.id, l]));
  return kennzeichen.filter((k) => {
    const land = landZu.get(k.countryId);
    if (!land) return false;
    if (k.scope === "country") return reiter === "europa";
    if (reiter === "deutschland") return land.iso2 === "DE";
    if (reiter === "europa")
      return land.iso2 !== "DE" && land.continent === "europa";
    return land.continent === "andere";
  });
}

/**
 * Der Bereich als ein Adressteil. Der Bundeslandname steht unveraendert
 * darin ("bundesland-Baden-Württemberg"); fuer die Adresszeile kodiert ihn
 * der Aufrufer, react-router gibt ihn dekodiert zurueck. Deshalb wird hier
 * weder kodiert noch dekodiert, sonst passierte es zweimal.
 */
export function bereichSchluessel(bereich: Bereich): string {
  switch (bereich.art) {
    case "land":
      return `land-${bereich.iso2}`;
    case "bundesland":
      return `bundesland-${bereich.name}`;
    case "fehlend":
      return `fehlend-${bereich.reiter}`;
    default:
      return bereich.art;
  }
}

export function bereichAusSchluessel(schluessel: string): Bereich | undefined {
  if (
    schluessel === "bundesweit" ||
    schluessel === "diplomaten" ||
    schluessel === "laenderkennzeichen"
  ) {
    return { art: schluessel };
  }
  // Nur am ERSTEN Bindestrich trennen: Bundeslandnamen tragen selbst
  // welche ("Baden-Württemberg", "Sachsen-Anhalt").
  const trenner = schluessel.indexOf("-");
  if (trenner < 0) return undefined;
  const art = schluessel.slice(0, trenner);
  const rest = schluessel.slice(trenner + 1);
  if (rest.length === 0) return undefined;
  if (art === "land") return { art: "land", iso2: rest };
  if (art === "bundesland") return { art: "bundesland", name: rest };
  if (art === "fehlend") {
    const reiter = REITER_SCHLUESSEL.find((r) => r === rest);
    return reiter === undefined ? undefined : { art: "fehlend", reiter };
  }
  return undefined;
}

/** Die Ueberschrift der Unterseite, wie in der Zeile davor. */
export function bereichTitel(bereich: Bereich, laender: Land[]): string {
  switch (bereich.art) {
    case "land":
      return (
        laender.find((l) => l.iso2 === bereich.iso2)?.nameDe ?? bereich.iso2
      );
    case "bundesland":
      return bereich.name;
    case "bundesweit":
      return "Bundesweit";
    case "diplomaten":
      return "Diplomatenkennzeichen";
    case "laenderkennzeichen":
      return "Länderkennzeichen";
    case "fehlend":
      return "Nur in der eigenen Sammlung";
  }
}

/**
 * Sortierung der Listen: natuerlich, nicht nach sortKey.
 *
 * sortKey sortiert erst nach Laenge, dann alphabetisch - das ist die
 * Reihenfolge beim Durchblaettern der Treffer (treffer.ts), nicht die der
 * Listen. Im Original stehen "0-1, 0-2, 0-3, 0-4, 0-10, 0-12" untereinander
 * (docs/reference/statistik-detail-de-nichtgesehen.png) und die Kreiszeichen
 * rein alphabetisch ("BEL, CA, GRS, HZ, JB"), also numerisch bewusster
 * Vergleich statt Laengenstaffel.
 */
const NATUERLICH = new Intl.Collator("de", { numeric: true });

/**
 * Alle Kennzeichen eines Bereichs, fertig sortiert.
 *
 * Deckungsgleich mit dem, was die Zeile zaehlt: fortschrittFuer() fuer Land
 * und Laenderkennzeichen, bundeslandRinge() fuer ein Bundesland. Beim
 * Bundesland zaehlt fuer die Zugehoerigkeit die volle Liste (stateName),
 * nicht die heutige (stateNameCurrent) - HZ steht deshalb in Brandenburg
 * mit in der Liste, so wie im Original.
 */
export function kennzeichenImBereich(
  bereich: Bereich,
  kennzeichen: Kennzeichen[],
  laender: Land[],
): Kennzeichen[] {
  const deutschlandId = laender.find((l) => l.iso2 === "DE")?.id;
  const reiterIds =
    bereich.art === "fehlend"
      ? new Set(
          reiterKennzeichen(bereich.reiter, kennzeichen, laender).map(
            (k) => k.id,
          ),
        )
      : new Set<number>();
  const passt = (k: Kennzeichen): boolean => {
    switch (bereich.art) {
      case "land": {
        const land = laender.find((l) => l.iso2 === bereich.iso2);
        return (
          land !== undefined && k.countryId === land.id && k.scope !== "country"
        );
      }
      case "bundesland":
        return (
          k.countryId === deutschlandId &&
          k.scope !== "country" &&
          (k.stateName?.split(", ").includes(bereich.name) ?? false)
        );
      case "bundesweit":
        return k.countryId === deutschlandId && k.scope === "federal";
      case "diplomaten":
        return k.countryId === deutschlandId && k.scope === "diplomatic";
      case "laenderkennzeichen":
        return k.scope === "country";
      case "fehlend":
        return reiterIds.has(k.id);
    }
  };
  // Innerhalb eines Landes natuerlich, ueber Laender hinweg in der
  // Reihenfolge der Stammdaten: unter EUROPA stuenden sonst die Codes
  // verschiedener Laender ineinander verkaemmt.
  const rang = new Map(laender.map((l) => [l.id, l.sortOrder]));
  return kennzeichen
    .filter(passt)
    .sort(
      (a, b) =>
        (rang.get(a.countryId) ?? 0) - (rang.get(b.countryId) ?? 0) ||
        NATUERLICH.compare(a.code, b.code),
    );
}

/**
 * Die beiden Toepfe unter der Deutschland-Zeile: "Bundesweit" (62) und
 * "Diplomatenkennzeichen" (218 in der App, 221 bei uns - siehe
 * docs/screenshots-benoetigt.md). Sie haben kein Bundesland und fielen
 * deshalb bisher zwischen Deutschland-Zeile und Bundeslandliste hindurch,
 * obwohl das Original sie fuehrt (docs/reference/
 * statistik-deutschland-2026-09-08.png).
 */
export function sonderRinge(
  kennzeichen: Kennzeichen[],
  laender: Land[],
  eintraege: Eintragskarte,
): RingZeile[] {
  const bereiche: Bereich[] = [{ art: "bundesweit" }, { art: "diplomaten" }];
  return bereiche.map((bereich) => {
    const passende = kennzeichenImBereich(bereich, kennzeichen, laender);
    return {
      name: bereichTitel(bereich, laender),
      gesehen: passende.filter((k) => eintraege.has(k.id)).length,
      gesamt: passende.length,
      bereich,
    };
  });
}
